// Ski mountains for the Outdoors screen: new snow from the nearest USDA SNOTEL
// gauge, the NWS snowfall forecast, the drive and the chain/traction laws and
// closures on the way (CDOT COtrip), and a powder-day call. The catalog ties
// each resort to its gauge and to the highways between the Front Range and it,
// so "on the way" means those roads rather than a straight line.

import type { SkyStamp } from "./sky/types";

type LatLon = [lat: number, lon: number];

// Corridor waypoints, in driving order from the Denver metro.
const IDAHO_SPRINGS: LatLon = [39.742, -105.513];
const EMPIRE: LatLon = [39.759, -105.684];
const BERTHOUD_PASS: LatLon = [39.798, -105.777];
const EISENHOWER: LatLon = [39.678, -105.912];
const SILVERTHORNE: LatLon = [39.63, -106.07];
const FRISCO: LatLon = [39.577, -106.097];
const VAIL_PASS: LatLon = [39.531, -106.217];
const GRANBY: LatLon = [40.086, -105.94];
const KREMMLING: LatLon = [40.059, -106.389];
const RABBIT_EARS: LatLon = [40.384, -106.611];
const GLENWOOD: LatLon = [39.55, -107.325];
const LEADVILLE: LatLon = [39.25, -106.29];
const NEDERLAND: LatLon = [39.961, -105.511];
const FAIRPLAY: LatLon = [39.225, -105.999];
const BUENA_VISTA: LatLon = [38.842, -106.131];
const PONCHA: LatLon = [38.515, -106.077];
const GUNNISON: LatLon = [38.545, -106.925];
const MONTROSE: LatLon = [38.478, -107.876];
const SOUTH_FORK: LatLon = [37.67, -106.62];

export interface Resort {
  id: string;
  name: string;
  lat: number;
  lon: number;
  /** SNOTEL station triplet ("335:CO:SNTL") at or near the mountain. */
  snotel: string;
  snotelName: string;
  /** Highways from the Front Range, as waypoints; chain laws and closures near them count. */
  via: LatLon[];
  /** The resort's own snow report, for the full picture (lifts, grooming). */
  report: string;
  /** Mountain-town papers that cover it (News screen source ids), where the Resorts screen finds its news. */
  papers?: string[];
  /** Other names stories use for it ("A-Basin"). */
  aliases?: string[];
  /**
   * False when the report page refuses to show inside another site
   * (X-Frame-Options), so Kinmantle offers it as a QR code instead. Checked
   * from a browser 2026-10-26 (Vail Resorts' sites answer scripts with a bot
   * page, so only a real browser shows their headers); the hosts that do embed
   * are allowed in public/_headers frame-src.
   */
  frameable?: false;
  /**
   * The resort's MtnPowder feed ids (mtnpowder.com/feed?resortId=), where
   * its lifts and trails, open or not, come from; one per mountain for Aspen
   * Snowmass. Checked 2026-10-31: Copper, Eldora and Aspen's feeds carried
   * only a forecast then, and the Vail Resorts mountains publish none.
   */
  mtnpowder?: number[];
}

const I70: LatLon[] = [IDAHO_SPRINGS, EISENHOWER, SILVERTHORNE];

export const RESORTS: Resort[] = [
  { id: "winter-park", name: "Winter Park", lat: 39.886, lon: -105.763, snotel: "335:CO:SNTL", snotelName: "Berthoud Summit", via: [IDAHO_SPRINGS, EMPIRE, BERTHOUD_PASS], report: "https://www.winterparkresort.com/the-mountain/mountain-report", papers: ["sky-hi-news"], mtnpowder: [5] },
  { id: "loveland", name: "Loveland", lat: 39.68, lon: -105.898, snotel: "602:CO:SNTL", snotelName: "Loveland Basin", via: [IDAHO_SPRINGS], report: "https://skiloveland.com/snow-report/", papers: ["summit-daily"], aliases: ["Loveland Ski Area", "Loveland Pass"] },
  { id: "arapahoe-basin", name: "Arapahoe Basin", lat: 39.642, lon: -105.872, snotel: "505:CO:SNTL", snotelName: "Grizzly Peak", via: [IDAHO_SPRINGS, EISENHOWER], report: "https://www.arapahoebasin.com/snow-report/", papers: ["summit-daily"], aliases: ["A-Basin"], mtnpowder: [93] },
  { id: "keystone", name: "Keystone", lat: 39.605, lon: -105.954, snotel: "505:CO:SNTL", snotelName: "Grizzly Peak", via: [...I70], report: "https://www.keystoneresort.com/the-mountain/mountain-conditions/snow-and-weather-report.aspx", frameable: false, papers: ["summit-daily"] },
  { id: "breckenridge", name: "Breckenridge", lat: 39.48, lon: -106.067, snotel: "531:CO:SNTL", snotelName: "Hoosier Pass", via: [...I70, FRISCO], report: "https://www.breckenridge.com/the-mountain/mountain-conditions/snow-and-weather-report.aspx", frameable: false, papers: ["summit-daily"], aliases: ["Breck"] },
  { id: "copper", name: "Copper Mountain", lat: 39.502, lon: -106.151, snotel: "415:CO:SNTL", snotelName: "Copper Mountain", via: [...I70, FRISCO], report: "https://www.coppercolorado.com/the-mountain/conditions-weather/snow-report/", papers: ["summit-daily"], aliases: ["Copper"], mtnpowder: [55] },
  { id: "vail", name: "Vail", lat: 39.606, lon: -106.355, snotel: "842:CO:SNTL", snotelName: "Vail Mountain", via: [...I70, FRISCO, VAIL_PASS], report: "https://www.vail.com/the-mountain/mountain-conditions/snow-and-weather-report.aspx", frameable: false, papers: ["vail-daily"] },
  { id: "beaver-creek", name: "Beaver Creek", lat: 39.604, lon: -106.517, snotel: "1041:CO:SNTL", snotelName: "Beaver Ck Village", via: [...I70, FRISCO, VAIL_PASS], report: "https://www.beavercreek.com/the-mountain/mountain-conditions/snow-and-weather-report.aspx", frameable: false, papers: ["vail-daily"] },
  { id: "eldora", name: "Eldora", lat: 39.937, lon: -105.583, snotel: "564:CO:SNTL", snotelName: "Lake Eldora", via: [NEDERLAND], report: "https://www.eldora.com/the-mountain/current-conditions/snow-report-weather/", mtnpowder: [56] },
  { id: "steamboat", name: "Steamboat", lat: 40.457, lon: -106.805, snotel: "825:CO:SNTL", snotelName: "Tower", via: [IDAHO_SPRINGS, EMPIRE, BERTHOUD_PASS, GRANBY, KREMMLING, RABBIT_EARS], report: "https://www.steamboat.com/the-mountain/mountain-report", papers: ["steamboat-pilot"], mtnpowder: [6] },
  { id: "granby-ranch", name: "Granby Ranch", lat: 40.045, lon: -105.906, snotel: "1187:CO:SNTL", snotelName: "High Lonesome", via: [IDAHO_SPRINGS, EMPIRE, BERTHOUD_PASS, GRANBY], report: "https://granbyranch.com/mountain-info/", papers: ["sky-hi-news"] },
  { id: "ski-cooper", name: "Ski Cooper", lat: 39.361, lon: -106.301, snotel: "485:CO:SNTL", snotelName: "Fremont Pass", via: [...I70, FRISCO, LEADVILLE], report: "https://www.skicooper.com/snow-trail-report/", papers: ["vail-daily", "summit-daily"] },
  { id: "aspen-snowmass", name: "Aspen Snowmass", lat: 39.209, lon: -106.95, snotel: "542:CO:SNTL", snotelName: "Independence Pass", via: [...I70, FRISCO, VAIL_PASS, GLENWOOD], report: "https://www.aspensnowmass.com/four-mountains/snow-report", frameable: false, papers: ["aspen-times"], aliases: ["Aspen Mountain", "Snowmass", "Aspen Highlands", "Buttermilk", "Aspen Skiing"], mtnpowder: [52, 54, 51, 53] },
  { id: "monarch", name: "Monarch", lat: 38.512, lon: -106.332, snotel: "701:CO:SNTL", snotelName: "Porphyry Creek", via: [FAIRPLAY, BUENA_VISTA, PONCHA], report: "https://skimonarch.com/conditions/", frameable: false },
  { id: "crested-butte", name: "Crested Butte", lat: 38.899, lon: -106.965, snotel: "380:CO:SNTL", snotelName: "Butte", via: [FAIRPLAY, BUENA_VISTA, PONCHA, GUNNISON], report: "https://www.skicb.com/the-mountain/mountain-conditions/weather-report.aspx", frameable: false, papers: ["crested-butte-news"], aliases: ["Crested Butte Mountain Resort", "CBMR"] },
  { id: "telluride", name: "Telluride", lat: 37.937, lon: -107.812, snotel: "1344:CO:SNTL", snotelName: "Alta Lakes", via: [FAIRPLAY, BUENA_VISTA, PONCHA, GUNNISON, MONTROSE], report: "https://tellurideskiresort.com/snow-report/", papers: ["telluride-planet"] },
  { id: "wolf-creek", name: "Wolf Creek", lat: 37.472, lon: -106.793, snotel: "874:CO:SNTL", snotelName: "Wolf Creek Summit", via: [FAIRPLAY, BUENA_VISTA, SOUTH_FORK], report: "https://wolfcreekski.com/wcs-snow-report/", papers: ["durango-herald"] },
  { id: "purgatory", name: "Purgatory", lat: 37.63, lon: -107.814, snotel: "387:CO:SNTL", snotelName: "Cascade #2", via: [FAIRPLAY, BUENA_VISTA, SOUTH_FORK], report: "https://www.purgatory.ski/mountain/weather-conditions-webcams/", frameable: false, papers: ["durango-herald"], aliases: ["Purgatory Resort"] },
  { id: "powderhorn", name: "Powderhorn", lat: 39.069, lon: -108.15, snotel: "622:CO:SNTL", snotelName: "Mesa Lakes", via: [...I70, FRISCO, VAIL_PASS, GLENWOOD], report: "https://www.powderhorn.com/conditions/", papers: ["post-independent"] },
  { id: "echo-mountain", name: "Echo Mountain", lat: 39.684, lon: -105.519, snotel: "936:CO:SNTL", snotelName: "Echo Lake", via: [IDAHO_SPRINGS], report: "https://echomtn.com/mountain-conditions" },
];

export const MAX_RESORTS = 6;

/**
 * Logos where a resort publishes a good one (checked 2026-10-26: 128 px or more).
 * Others use the site's icon via Google's favicon service, and a monogram when
 * that is too small to look right (MountainsWidget.tsx).
 */
export const RESORT_LOGOS: Record<string, string> = {
  // Winter Park's wordmark from its site header; A-Basin's classic logo from Wikipedia
  // (its site shows an 80th-anniversary badge this season).
  "winter-park": "https://www.winterparkresort.com/-/media/winter-park/logos/wp-desktop.svg",
  "arapahoe-basin": "https://upload.wikimedia.org/wikipedia/en/3/34/Arapahoe_Basin_logo.png",
  copper: "https://www.coppercolorado.com/icons/icon-512x512.png",
  eldora: "https://www.eldora.com/icons/icon-512x512.png",
  loveland: "https://skiloveland.com/wp-content/uploads/2018/12/favicon2.png",
  "aspen-snowmass": "https://www.aspensnowmass.com/-/media/aspen-snowmass/images/mobile-apps/app-icon.png?h=126&w=126",
  "wolf-creek": "https://wolfcreekski.com/wp-content/uploads/2021/08/cropped-logo_circle_1200x1200-192x192.png",
  monarch: "https://skimonarch.com/wp-content/uploads/2023/08/cropped-monarch-logo-512-1-192x192.png",
  purgatory: "https://www.purgatory.ski/wp-content/uploads/sites/2/fbrfg/apple-touch-icon.png",
  powderhorn: "https://www.powderhorn.com/wp-content/uploads/2026/08/ph_site_icon-300x300.png",
  "granby-ranch": "https://granbyranch.com/wp-content/uploads/2026/09/cropped-0-300x300.jpg",
  "ski-cooper": "https://www.skicooper.com/wp-content/uploads/2024/08/cropped-cooper-512-192x192.png",
  steamboat: "https://commons.wikimedia.org/wiki/Special:FilePath/Steamboat_Ski_Resort_logo.svg?width=256",
};

export interface LogoSource {
  src: string;
  /** A site icon from Google's service: too small to use below 40 px. */
  icon: boolean;
}

/** Pictures to try for a resort's logo, best first. */
export function resortLogos(r: { id: string; report: string }): LogoSource[] {
  const host = new URL(r.report).hostname;
  const own = RESORT_LOGOS[r.id];
  return [
    ...(own ? [{ src: own, icon: false }] : []),
    { src: `https://www.google.com/s2/favicons?domain=${host}&sz=128`, icon: true },
  ];
}

export function resortById(id: string): Resort | undefined {
  return RESORTS.find((r) => r.id === id);
}

export interface MountainSettings {
  /** Resort ids from RESORTS, in the household's order. */
  resorts: string[];
  /** New snow (in) that makes a powder day. */
  powderInches: number;
}

export const DEFAULT_MOUNTAINS: MountainSettings = { resorts: [], powderInches: 6 };

/** A chain/traction law or closure on the way to one of the resorts. */
export interface RoadEvent {
  id: string;
  kind: "chain" | "traction" | "closure";
  route: string;
  title: string;
  /** "between Winter Park and Empire" */
  where: string | null;
  /** Resorts it is on the way to. */
  resorts: string[];
}

export interface ResortReport {
  id: string;
  name: string;
  snotelName: string;
  report: string;
  /** Snow depth at the gauge now, inches. */
  depthIn: number | null;
  newSnow24In: number | null;
  newSnow48In: number | null;
  /** NWS forecast snowfall at the mountain, inches. */
  forecast24In: number | null;
  forecast48In: number | null;
  /** When the gauge last reported. */
  observedAt: string | null;
  /** CDOT's routing estimate from home, minutes. */
  driveMin: number | null;
  powder: "today" | "tomorrow" | null;
}

export interface MountainsPayload {
  resorts: ResortReport[];
  roads: RoadEvent[];
  powderInches: number;
  stamp: SkyStamp;
}

/**
 * New snow from hourly gauge depths (inches, oldest first): today's depth less
 * the lowest depth in the window before it. Depth gauges jitter by an inch or
 * so, so readings are smoothed over three hours and gains under 2″ don't count.
 */
export function newSnowFrom(depths: { t: number; in: number }[], now: number, hours: number): number | null {
  const sorted = depths.filter((d) => Number.isFinite(d.in)).sort((a, b) => a.t - b.t);
  if (sorted.length < 3) return null;
  const smooth = sorted.map((d, i) => {
    const w = sorted.slice(Math.max(0, i - 2), i + 1).map((x) => x.in).sort((a, b) => a - b);
    return { t: d.t, in: w[Math.floor(w.length / 2)]! };
  });
  const latest = smooth[smooth.length - 1]!;
  // A gauge silent for half a day can't say what fell.
  if (now - latest.t > 12 * 3_600_000) return null;
  const window = smooth.filter((d) => d.t >= now - hours * 3_600_000);
  if (window.length < 2) return null;
  const low = Math.min(...window.map((d) => d.in));
  const gain = latest.in - low;
  return gain >= 2 ? Math.round(gain) : 0;
}

/** Powder today when the gauge caught it overnight; tomorrow when the next 24 h forecast says so. */
export function powderCall(r: Pick<ResortReport, "newSnow24In" | "forecast24In">, inches: number): ResortReport["powder"] {
  if ((r.newSnow24In ?? 0) >= inches) return "today";
  if ((r.forecast24In ?? 0) >= inches) return "tomorrow";
  return null;
}

/** Distance (km) from a point to a polyline, equirectangular (fine at a few hundred km). */
export function kmToPath(p: LatLon, path: LatLon[]): number {
  const kx = 111.32 * Math.cos((p[0] * Math.PI) / 180);
  const ky = 110.57;
  const xy = ([lat, lon]: LatLon) => [(lon - p[1]) * kx, (lat - p[0]) * ky] as const;
  let best = Infinity;
  for (let i = 0; i < path.length; i++) {
    const a = xy(path[i]!);
    const b = xy(path[Math.min(i + 1, path.length - 1)]!);
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const len2 = dx * dx + dy * dy;
    const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, -(a[0] * dx + a[1] * dy) / len2));
    best = Math.min(best, Math.hypot(a[0] + t * dx, a[1] + t * dy));
  }
  return best;
}

/** The road from home to a resort, for matching CDOT events. */
export function corridorOf(resort: Resort, home: { lat: number; lon: number } | null): LatLon[] {
  return [...(home ? [[home.lat, home.lon] as LatLon] : []), ...resort.via, [resort.lat, resort.lon]];
}
